import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Assignment,
  ExpandMore,
  Refresh,
  Download,
  Language,
  Today,
  DateRange,
  Person,
  Email,
  Phone,
  Message,
  TableChart,
  Launch,
  Share,
  Sync
} from '@mui/icons-material';

const Forms = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorSeverity, setErrorSeverity] = useState('error');
  const [lastSync, setLastSync] = useState(null);
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, emails: '', type: 'sync' });
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSites = async (preserveSelection = false) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sites/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        
        // Only set default selection if not preserving and no current selection
        if (!preserveSelection && data.length > 0 && !selectedSite) {
          setSelectedSite(data[0].id);
        }
      } else {
        setError('Failed to fetch sites');
      }
    } catch (err) {
      setError('Network error while fetching sites');
    }
  };

  const fetchFormEntries = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError('');

    try {
      // Use backend API endpoint instead of direct WordPress access
      const params = new URLSearchParams({
        from_date: `${fromDate} 00:00:00`,
        to_date: `${toDate} 23:59:59`
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/${selectedSite}?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(data);
        setLastSync(new Date());
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch form entries: ${response.status} ${errorText}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
    setFormData(null); // Clear previous data
  };

  const getTotalEntries = () => {
    if (!formData?.data) return 0;
    
    let total = 0;
    Object.values(formData.data).forEach(formType => {
      Object.values(formType).forEach(formEntries => {
        total += formEntries.length;
      });
    });
    return total;
  };

  const formatFieldValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return JSON.stringify(value);
    }
    return value || '-';
  };

  const getFieldIcon = (fieldName) => {
    const name = fieldName.toLowerCase();
    if (name.includes('email')) return <Email fontSize="small" />;
    if (name.includes('phone') || name.includes('tel')) return <Phone fontSize="small" />;
    if (name.includes('name')) return <Person fontSize="small" />;
    if (name.includes('message') || name.includes('comment')) return <Message fontSize="small" />;
    return null;
  };

  const exportData = () => {
    if (!formData?.data) return;
    
    // Convert to CSV
    const csvData = [];
    csvData.push(['Form Type', 'Form Name', 'Date Submitted', 'Source URL', 'IP Address', 'Fields']);
    
    Object.entries(formData.data).forEach(([formType, forms]) => {
      Object.entries(forms).forEach(([formName, entries]) => {
        entries.forEach(entry => {
          const fields = Object.entries(entry)
            .filter(([key]) => !['date_submitted', 'source_url', 'ip', 'ID', 'post_title', 'post_date', 'url'].includes(key))
            .map(([key, value]) => `${key}: ${formatFieldValue(value)}`)
            .join(' | ');
          
          csvData.push([
            formType.replace('_', ' ').toUpperCase(),
            formName,
            entry.date_submitted || entry.post_date || '-',
            entry.source_url || entry.url || '-',
            entry.ip || '-',
            fields
          ]);
        });
      });
    });
    
    const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form-entries-${selectedSite}-${fromDate}-to-${toDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSyncWithEmails = async (shareEmails = null) => {
    setSyncingSheet(true);
    setError('');

    // Use provided emails or existing emails from database
    const emailsToUse = shareEmails || (() => {
      try {
        return JSON.parse(selectedSiteData?.google_sheet_shared_emails || '[]');
      } catch {
        return [];
      }
    })();

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${selectedSiteData.id}/create-google-sheet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from_date: `${fromDate} 00:00:00`,
          to_date: `${toDate} 23:59:59`,
          share_emails: emailsToUse
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Display progress information
        if (data.progress) {
          const { successful, failed, total_sheets } = data.progress;

          if (data.partial_sync) {
            // Partial success - show warning with details
            const failedSheets = failed.map(s => s.name).join(', ');
            const hasQuotaError = failed.some(s => s.is_quota_error);

            let message = `Partial sync: ${successful.length}/${total_sheets} sheets synced successfully.\n\n`;
            message += `Failed sheets: ${failedSheets}\n\n`;

            if (hasQuotaError) {
              message += 'Some sheets failed due to Google API quota limits. Wait 60 seconds and click "Sync Now" again to retry the failed sheets.';
            }

            setErrorSeverity('warning');
            setError(message);
          } else if (successful.length === total_sheets && total_sheets > 0) {
            // Full success
            setError('');
            setErrorSeverity('error');
          }
        }

        // Refresh site data to get updated Google Sheets info, preserving selection
        await fetchSites(true);
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || 'Unknown error';
        } catch {
          errorMessage = await response.text();
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError(`Network error while syncing Google Sheet: ${err.message}`);
    } finally {
      setSyncingSheet(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailDialogSubmit = () => {
    // Parse comma-separated emails and trim whitespace
    const emailList = emailDialog.emails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
    
    // Validate emails
    const invalidEmails = emailList.filter(email => !validateEmail(email));
    if (invalidEmails.length > 0) {
      setError(`Invalid email addresses: ${invalidEmails.join(', ')}. Please enter valid email addresses.`);
      return;
    }
    
    // Close dialog
    setEmailDialog({ open: false, emails: '', type: 'sync' });
    
    if (emailDialog.type === 'sync') {
      handleSyncWithEmails(emailList);
    } else {
      // Handle create case (existing functionality)
      handleCreateWithEmails(emailList);
    }
  };

  const handleCreateWithEmails = async (shareEmails) => {
    setCreatingSheet(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${selectedSiteData.id}/create-google-sheet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from_date: `${fromDate} 00:00:00`,
          to_date: `${toDate} 23:59:59`,
          share_emails: shareEmails
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Display progress information
        if (data.progress) {
          const { successful, failed, total_sheets } = data.progress;

          if (data.partial_sync) {
            // Partial success - show warning with details
            const failedSheets = failed.map(s => s.name).join(', ');
            const hasQuotaError = failed.some(s => s.is_quota_error);

            let message = `Google Sheet created! Partial sync: ${successful.length}/${total_sheets} sheets synced successfully.\n\n`;
            message += `Failed sheets: ${failedSheets}\n\n`;

            if (hasQuotaError) {
              message += 'Some sheets failed due to Google API quota limits. Wait 60 seconds and click "Sync Now" to retry the failed sheets.';
            }

            setErrorSeverity('warning');
            setError(message);
          } else {
            setError('');
            setErrorSeverity('error');
          }
        } else {
          setError('');
        }

        // Refresh site data, preserving selection
        await fetchSites(true);

        // Open the spreadsheet in a new tab
        if (data.spreadsheet_url) {
          window.open(data.spreadsheet_url, '_blank');
        }
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || 'Unknown error';
        } catch {
          errorMessage = await response.text();
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError(`Network error while creating Google Sheet: ${err.message}`);
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleEmailDialogCancel = () => {
    setEmailDialog({ open: false, emails: '', type: 'sync' });
  };

  const selectedSiteData = sites.find(s => s.id === selectedSite);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Assignment sx={{ fontSize: 48, mr: 3, opacity: 0.9 }} />
            <Box>
              <Typography variant="h3" component="h1" fontWeight="600" sx={{ mb: 1 }}>
                Form Entries Dashboard
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Comprehensive form submission analytics and management
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Controls */}
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
            Filter & Search Options
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel sx={{ fontWeight: 500 }}>Select Website</InputLabel>
                <Select
                  value={selectedSite}
                  onChange={handleSiteChange}
                  label="Select Website"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Language sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {site.rootdomain}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {site.website_url}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: <DateRange sx={{ mr: 1, color: 'primary.main' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: <Today sx={{ mr: 1, color: 'primary.main' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchFormEntries}
                disabled={loading || !selectedSite}
                startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
                size="large"
                sx={{ 
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                {loading ? 'Fetching...' : 'Fetch Data'}
              </Button>
            </Grid>
          </Grid>
          
          {selectedSiteData && (
            <Box sx={{ 
              mt: 3, 
              pt: 3, 
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Selected Website:
              </Typography>
              <Chip
                icon={<Language />}
                label={selectedSiteData.website_url}
                variant="outlined"
                color="primary"
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    fontSize: 18
                  }
                }}
              />
              {lastSync && (
                <Chip
                  icon={<Refresh />}
                  label={`Last synced: ${lastSync.toLocaleTimeString()}`}
                  color="success"
                  variant="filled"
                  sx={{ 
                    borderRadius: 2,
                    fontWeight: 500
                  }}
                />
              )}
            </Box>
          )}
        </Paper>

        {/* Google Sheets Integration */}
        {selectedSiteData && (
          <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TableChart sx={{ fontSize: 28, mr: 2, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Google Sheets Integration
              </Typography>
            </Box>

            {selectedSiteData.google_sheet_id ? (
              <Box>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ 
                      p: 3, 
                      bgcolor: 'success.light', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'success.main'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TableChart sx={{ mr: 2, color: 'success.dark' }} />
                        <Typography variant="body1" fontWeight={600} color="success.dark">
                          Connected to Google Sheets
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="success.dark" sx={{ mb: 2 }}>
                        Form submissions are automatically synced to your Google Sheet
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<Share />}
                          label={`Shared with: ${(() => {
                            try {
                              const emails = JSON.parse(selectedSiteData.google_sheet_shared_emails || '[]');
                              return Array.isArray(emails) ? emails.join(', ') : 'No shared emails';
                            } catch {
                              return selectedSiteData.google_sheet_shared_emails || 'No shared emails';
                            }
                          })()}`}
                          variant="outlined"
                          color="success"
                          sx={{ borderRadius: 2 }}
                        />
                        {selectedSiteData.google_sheet_last_updated && (
                          <Chip
                            icon={<Sync />}
                            label={`Last updated: ${new Date(selectedSiteData.google_sheet_last_updated).toLocaleString()}`}
                            variant="filled"
                            color="success"
                            sx={{ borderRadius: 2 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Launch />}
                        onClick={() => window.open(selectedSiteData.google_sheet_url, '_blank')}
                        sx={{ 
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none'
                        }}
                      >
                        Open in Google Sheets
                      </Button>
                      
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={syncingSheet ? <CircularProgress size={20} /> : <Sync />}
                        onClick={() => {
                          // Show email dialog with current shared emails pre-filled
                          const currentEmails = (() => {
                            try {
                              const emails = JSON.parse(selectedSiteData?.google_sheet_shared_emails || '[]');
                              return Array.isArray(emails) ? emails.join(', ') : '';
                            } catch {
                              return '';
                            }
                          })();
                          
                          setEmailDialog({ 
                            open: true, 
                            emails: currentEmails,
                            type: 'sync' 
                          });
                        }}
                        disabled={syncingSheet}
                        sx={{ 
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none'
                        }}
                      >
                        {syncingSheet ? 'Syncing...' : 'Sync Now'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{ 
                p: 3, 
                bgcolor: 'info.light', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.main',
                textAlign: 'center'
              }}>
                <TableChart sx={{ fontSize: 48, color: 'info.dark', mb: 2, opacity: 0.7 }} />
                <Typography variant="h6" color="info.dark" sx={{ mb: 2, fontWeight: 600 }}>
                  Google Sheets Not Connected
                </Typography>
                <Typography variant="body2" color="info.dark" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                  Connect this site to Google Sheets to automatically export form submissions to a shareable spreadsheet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={creatingSheet ? <CircularProgress size={20} /> : <TableChart />}
                  onClick={() => {
                    setEmailDialog({ 
                      open: true, 
                      emails: '',
                      type: 'create' 
                    });
                  }}
                  disabled={creatingSheet}
                  sx={{ 
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  {creatingSheet ? 'Creating...' : 'Create Google Sheet'}
                </Button>
              </Box>
            )}
          </Paper>
        )}


        {/* Error/Warning Alert */}
        {error && (
          <Alert
            severity={errorSeverity}
            sx={{
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500,
                whiteSpace: 'pre-line'
              }
            }}
            onClose={() => {
              setError('');
              setErrorSeverity('error');
            }}
          >
            {error}
          </Alert>
        )}

        {/* Results */}
        {formData && (
          <Box>
            {/* Summary */}
            <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                  <Typography variant="h5" fontWeight="600" color="text.primary" sx={{ mb: 1 }}>
                    Analytics Summary
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {formData.fromDate} to {formData.toDate}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={exportData}
                  disabled={getTotalEntries() === 0}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    }
                  }}
                >
                  Export to CSV
                </Button>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: 3
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="h3" fontWeight="700" sx={{ mb: 1 }}>
                        {getTotalEntries()}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        Total Submissions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {Object.entries(formData.data).map(([formType, forms], index) => (
                  <Grid item xs={12} sm={6} md={3} key={formType}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        background: index % 2 === 0 
                          ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                          : 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
                        color: 'white',
                        borderRadius: 3
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="h3" fontWeight="700" sx={{ mb: 1 }}>
                          {Object.values(forms).reduce((sum, entries) => sum + entries.length, 0)}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          {formType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Form Entries */}
            {Object.entries(formData.data).map(([formType, forms]) => (
              <Paper key={formType} elevation={2} sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ 
                  p: 3, 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="h5" fontWeight="600">
                    {formType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Typography>
                  <Chip 
                    label={`${Object.values(forms).reduce((sum, entries) => sum + entries.length, 0)} submissions`}
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)', 
                      color: 'white',
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  />
                </Box>
                
                <Box sx={{ p: 2 }}>
                  {Object.entries(forms).map(([formName, entries]) => (
                    <Accordion 
                      key={`${formType}-${formName}`} 
                      sx={{ 
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                        '&:before': {
                          display: 'none',
                        },
                        '&.Mui-expanded': {
                          margin: '0 0 16px 0',
                        }
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: 'action.hover',
                          borderRadius: 2,
                          '&.Mui-expanded': {
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Assignment sx={{ mr: 2, color: 'primary.main' }} />
                          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                            {formName}
                          </Typography>
                          <Chip 
                            label={`${entries.length} submissions`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ 
                              mr: 2,
                              fontWeight: 500,
                              borderRadius: 2
                            }}
                          />
                        </Box>
                      </AccordionSummary>
                  
                      <AccordionDetails sx={{ p: 0 }}>
                        {entries.length > 0 ? (
                          <Box sx={{ p: 3 }}>
                            {entries.map((entry, index) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  mb: 3,
                                  p: 3,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.main'
                                  }
                                }}
                              >
                                {/* Entry Header */}
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  mb: 2,
                                  pb: 2,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }}>
                                  <Box>
                                    <Typography variant="body1" fontWeight={600} color="primary.main">
                                      Submission #{index + 1}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(entry.date_submitted || entry.post_date).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {entry.source_url || entry.url ? (
                                      <Chip 
                                        label={new URL(entry.source_url || entry.url).pathname}
                                        size="small" 
                                        variant="outlined"
                                        color="primary"
                                        sx={{ borderRadius: 2 }}
                                      />
                                    ) : null}
                                    {entry.ip && (
                                      <Chip 
                                        label={entry.ip}
                                        size="small" 
                                        variant="outlined"
                                        sx={{ borderRadius: 2 }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                
                                {/* Form Fields */}
                                <Grid container spacing={2}>
                                  {Object.entries(entry)
                                    .filter(([key]) => !['date_submitted', 'source_url', 'ip', 'ID', 'post_title', 'post_date', 'url', 'id', 'user_agent'].includes(key))
                                    .map(([key, value]) => (
                                      <Grid item xs={12} sm={6} key={key}>
                                        <Box sx={{
                                          p: 2,
                                          bgcolor: 'action.hover',
                                          borderRadius: 1,
                                          border: '1px solid',
                                          borderColor: 'grey.200'
                                        }}>
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                            {getFieldIcon(key) && (
                                              <Box sx={{ mr: 1, mt: 0.5 }}>
                                                {getFieldIcon(key)}
                                              </Box>
                                            )}
                                            <Typography variant="body2" fontWeight={600} color="primary.main">
                                              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </Typography>
                                          </Box>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              wordBreak: 'break-word',
                                              color: 'text.primary',
                                              minHeight: '1.5em'
                                            }}
                                          >
                                            {formatFieldValue(value) || '-'}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    ))}
                                </Grid>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="text.secondary" fontWeight={500}>
                              No entries found for this form.
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* No Data State */}
        {!loading && !formData && selectedSite && (
          <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Assignment sx={{ fontSize: 80, color: 'primary.light', mb: 3, opacity: 0.7 }} />
            <Typography variant="h5" color="text.primary" gutterBottom fontWeight={600}>
              Ready to Fetch Data
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Select your date range and click "Fetch Data" to load form entries from the selected website
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={fetchFormEntries}
              disabled={!selectedSite}
              startIcon={<Refresh />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Fetch Form Entries
            </Button>
          </Paper>
        )}

        {/* No Sites State */}
        {sites.length === 0 && (
          <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Language sx={{ fontSize: 80, color: 'primary.light', mb: 3, opacity: 0.7 }} />
            <Typography variant="h5" color="text.primary" gutterBottom fontWeight={600}>
              No Websites Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              You need to add websites first before you can view their form submissions
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="/add-site"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Add Your First Website
            </Button>
          </Paper>
        )}

        {/* Email Sharing Dialog */}
        <Dialog 
          open={emailDialog.open} 
          onClose={handleEmailDialogCancel}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            {emailDialog.type === 'sync' ? 'Update Google Sheet Sharing' : 'Share Google Sheet'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {emailDialog.type === 'sync' 
                ? 'Update the email addresses to share the Google Sheet with (comma-separated):'
                : 'Enter email addresses to share the Google Sheet with (comma-separated):'
              }
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Email Addresses"
              placeholder="user1@example.com, user2@example.com, user3@example.com"
              value={emailDialog.emails}
              onChange={(e) => setEmailDialog({ ...emailDialog, emails: e.target.value })}
              helperText="Separate multiple email addresses with commas. Leave empty to create without sharing."
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEmailDialogCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleEmailDialogSubmit}
              variant="contained"
              disabled={syncingSheet || creatingSheet}
            >
              {emailDialog.type === 'sync' 
                ? (syncingSheet ? 'Syncing...' : 'Sync & Update Sharing')
                : (creatingSheet ? 'Creating...' : 'Create & Share')
              }
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Forms;